import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const STORE_KEY = "chalann_store";
const USER_KEY = "chalann_user";

export function getStore() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function setStore(store) {
  localStorage.setItem(STORE_KEY, JSON.stringify(store));
}

export function clearStore() {
  localStorage.removeItem(STORE_KEY);
  localStorage.removeItem(USER_KEY);
}

export function getUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}
export function setUser(user) { localStorage.setItem(USER_KEY, JSON.stringify(user)); }
export function clearUser() { localStorage.removeItem(USER_KEY); }

export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const s = getStore();
  if (s?.id) config.headers["X-Store-Id"] = s.id;
  const u = getUser();
  if (u?.id) {
    config.headers["X-User-Id"] = u.id;
    config.headers["X-User-Name"] = u.name;
  }
  return config;
});

// ---- Store ----
export const createStore = (payload) => api.post("/stores", payload).then((r) => r.data);
export const listStores = () => api.get("/stores").then((r) => r.data);

// ---- Products ----
export const listProducts = () => api.get("/products").then((r) => r.data);
export const createProduct = (payload) => api.post("/products", payload).then((r) => r.data);
export const updateProduct = (id, payload) => api.put(`/products/${id}`, payload).then((r) => r.data);
export const deleteProduct = (id) => api.delete(`/products/${id}`).then((r) => r.data);
export const listProductsNeedingPricing = (category) =>
  api.get(`/products/needs-pricing${category ? `?category=${encodeURIComponent(category)}` : ""}`).then((r) => r.data);
export const bulkUpdatePrices = (items) => api.put(`/products/bulk-prices`, { items }).then((r) => r.data);
export const getChalannEnabled = () => api.get(`/store/chalann`).then((r) => r.data);
export const setChalannEnabled = (enabled) => api.put(`/store/chalann`, { enabled }).then((r) => r.data);
export const getOnboardingProgress = () => api.get(`/onboarding/progress`).then((r) => r.data);

/**
 * Normalize monetary values in a TTS string via backend money_speech.py.
 * Ensures Chalán says "ciento treinta pesos" instead of spelling digits.
 * Falls back to the raw text if the network call fails (never blocks speech).
 */
export const normalizeTTS = async (text) => {
  if (!text || !text.includes("$")) return text;
  try {
    const r = await api.post(`/tts/normalize`, { text });
    return r.data?.text || text;
  } catch {
    return text;
  }
};

export const ttsCanonical = async (kind, amount, customerName) => {
  try {
    const r = await api.post(`/tts/normalize`, { kind, amount, customer_name: customerName, text: "" });
    return r.data?.text || "";
  } catch {
    return "";
  }
};
export const aiLookupBarcode = (barcode) => api.get(`/products/ai-lookup?barcode=${encodeURIComponent(barcode)}`).then((r) => r.data);
export const productByBarcode = (code) => api.get(`/products/by-barcode?code=${encodeURIComponent(code)}`).then((r) => r.data);
export const visualScan = () => api.post("/products/visual-scan").then((r) => r.data);
export const localTzOffsetHours = () => -Math.round(new Date().getTimezoneOffset() / 60);
export const parseVoiceCommand = (text) =>
  api.post("/voice/parse-command", { text, tz_offset_hours: localTzOffsetHours() }).then((r) => r.data);
export const voiceTelemetry = () => api.get("/voice/telemetry").then((r) => r.data);
export const parsePriceDictation = (text) => api.post("/prices/parse-dictation", { text }).then((r) => r.data);
export const applyPriceUpdates = (items) => api.post("/prices/apply-updates", { items }).then((r) => r.data);

// ---- App users ----
export const listAppUsers = (businessId) => api.get(`/app-users?business_id=${businessId}`).then((r) => r.data);
export const createAppUser = (payload) => api.post("/app-users", payload).then((r) => r.data);
export const loginAppUser = (payload) => api.post("/app-users/login", payload).then((r) => r.data);
export const setAppUserPin = (userId, businessId, new_pin, current_pin = null) =>
  api.post(`/app-users/${userId}/set-pin?business_id=${businessId}`, { new_pin, current_pin }).then((r) => r.data);
export const deleteAppUser = (id, businessId) => api.delete(`/app-users/${id}?business_id=${businessId}`).then((r) => r.data);
export const getSyncVersion = () => api.get("/sync/version").then((r) => r.data);
export const dailyReport = () =>
  api.get("/reports/daily", { params: { tz_offset_hours: localTzOffsetHours() } }).then((r) => r.data);
export const getPublicReceipt = (saleId) => api.get(`/receipt/${saleId}`).then((r) => r.data);
export const getUsageToday = () => api.get("/usage/today").then((r) => r.data);
export const getSubscriptionStatus = () => api.get("/subscription/status").then((r) => {
  const s = getStore();
  if (s && r.data?.plan && s.plan !== r.data.plan) setStore({ ...s, plan: r.data.plan });
  return r.data;
});
export const getPlans = () => api.get("/plans").then((r) => r.data);
export const isOnePay = () => (getStore()?.plan || "chalanito") === "onepay";
export const getPaymentInstructions = (plan) =>
  api.get("/payment/instructions", { params: plan ? { plan } : {} }).then((r) => r.data);
export const reportPayment = (payload) => api.post("/payment/report", payload).then((r) => r.data);
export const createCheckout = (plan) =>
  api.post("/billing/checkout", { plan, origin_url: window.location.origin }).then((r) => r.data);
export const checkoutStatus = (sessionId) => api.get(`/billing/status/${sessionId}`).then((r) => r.data);
export const adminGetFxRate = () => api.get("/admin/settings/fx-rate", { headers: adminHeaders() }).then((r) => r.data);
export const adminSetFxRate = (usd_mxn_rate) =>
  api.put("/admin/settings/fx-rate", { usd_mxn_rate }, { headers: adminHeaders() }).then((r) => r.data);
export const listMyPayments = () => api.get("/payment/mine").then((r) => r.data);
export const parseRestockCommand = (text) => api.post("/voice/parse-restock", { text }).then((r) => r.data);
export const applyRestock = (items) => api.post("/voice/apply-restock", { items }).then((r) => r.data);
export const getInventoryReport = () => api.get("/reports/inventory").then((r) => r.data);
export const getDeliverySettings = () => api.get("/store/delivery").then((r) => r.data);
export const updateDeliverySettings = (payload) => api.put("/store/delivery", payload).then((r) => r.data);
export const updateDeliveryZones = (zones) => api.put("/store/delivery-zones", { zones }).then((r) => r.data);
export const getPublicDelivery = (storeId) => api.get(`/delivery/public/${storeId}`).then((r) => r.data);

// ---- Master catalog ----
export const listMasterProducts = (params = {}) => {
  const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""))).toString();
  return api.get(`/master-products${q ? `?${q}` : ""}`).then((r) => r.data);
};
export const getMasterFacets = () => api.get("/master-products/facets").then((r) => r.data);
export const adoptMasterProducts = (ids) => api.post("/master-products/adopt", { master_product_ids: ids }).then((r) => r.data);
export const adoptByBrand = (brand) => api.post(`/master-products/adopt-brand?brand=${encodeURIComponent(brand)}`).then((r) => r.data);
export const adoptMasterWithPrices = (items) =>
  api.post("/master-products/adopt-with-prices", { items }).then((r) => r.data);
export const lookupByGtin = (gtin) =>
  api.get(`/products/lookup/${encodeURIComponent(gtin)}`).then((r) => r.data);
export const adoptByCategory = (category, subcategory) =>
  api.post(`/master-products/adopt-category?category=${encodeURIComponent(category)}${subcategory ? `&subcategory=${encodeURIComponent(subcategory)}` : ""}`).then((r) => r.data);

// ---- Admin (single-operator console) ----
const ADMIN_KEY = "chalann_admin_token";
export const getAdminToken = () => localStorage.getItem(ADMIN_KEY);
export const setAdminToken = (t) => localStorage.setItem(ADMIN_KEY, t);
export const clearAdminToken = () => localStorage.removeItem(ADMIN_KEY);

const adminHeaders = () => ({ Authorization: `Bearer ${getAdminToken() || ""}` });

export const adminLogin = (phone, password) =>
  api.post("/admin/login", { phone, password }).then((r) => r.data);
export const adminStats = () => api.get("/admin/stats", { headers: adminHeaders() }).then((r) => r.data);
export const adminListStores = (params = {}) => {
  const q = new URLSearchParams(params).toString();
  return api.get(`/admin/stores${q ? `?${q}` : ""}`, { headers: adminHeaders() }).then((r) => r.data);
};
export const adminExtendTrial = (storeId, extraDays = 7) =>
  api.post(`/admin/stores/${storeId}/extend-trial`, { extra_days: extraDays }, { headers: adminHeaders() }).then((r) => r.data);
export const adminMarkPaid = (storeId) =>
  api.post(`/admin/stores/${storeId}/mark-paid`, {}, { headers: adminHeaders() }).then((r) => r.data);
export const adminSuspend = (storeId) =>
  api.post(`/admin/stores/${storeId}/suspend`, {}, { headers: adminHeaders() }).then((r) => r.data);
export const adminResume = (storeId) =>
  api.post(`/admin/stores/${storeId}/resume`, {}, { headers: adminHeaders() }).then((r) => r.data);
export const adminSetNotes = (storeId, notes) =>
  api.put(`/admin/stores/${storeId}/notes`, { admin_notes: notes }, { headers: adminHeaders() }).then((r) => r.data);
export const adminDeleteStore = (storeId) =>
  api.delete(`/admin/stores/${storeId}`, { headers: adminHeaders() }).then((r) => r.data);
export const adminListPayments = (status) =>
  api.get(`/admin/payments${status ? `?status=${status}` : ""}`, { headers: adminHeaders() }).then((r) => r.data);
export const adminConfirmPayment = (paymentId) =>
  api.post(`/admin/payments/${paymentId}/confirm`, {}, { headers: adminHeaders() }).then((r) => r.data);
export const adminRejectPayment = (paymentId) =>
  api.post(`/admin/payments/${paymentId}/reject`, {}, { headers: adminHeaders() }).then((r) => r.data);
export const adminListNotifications = (onlyUnread = false) =>
  api.get(`/admin/notifications${onlyUnread ? "?only_unread=true" : ""}`, { headers: adminHeaders() }).then((r) => r.data);
export const adminMarkNotificationRead = (id) =>
  api.post(`/admin/notifications/${id}/read`, {}, { headers: adminHeaders() }).then((r) => r.data);
export const adminAdvisorsReport = (onlyWithAdvisor = false) =>
  api.get(`/admin/advisors-report${onlyWithAdvisor ? "?only_with_advisor=true" : ""}`, { headers: adminHeaders() }).then((r) => r.data);
export const adminCommissionsReport = (month) =>
  api.get(`/admin/commissions-report${month ? `?month=${encodeURIComponent(month)}` : ""}`, { headers: adminHeaders() }).then((r) => r.data);

// ---- Admin · Master catalog CRUD ----
export const adminMasterStats = () =>
  api.get("/admin/master-catalog/stats", { headers: adminHeaders() }).then((r) => r.data);
export const adminMasterFacets = () => api.get("/master-products/facets").then((r) => r.data);
export const adminListMasterProducts = (params = {}) =>
  api.get("/admin/master-products", { params, headers: adminHeaders() }).then((r) => r.data);
export const adminCreateMasterProduct = (body) =>
  api.post("/admin/master-products", body, { headers: adminHeaders() }).then((r) => r.data);
export const adminUpdateMasterProduct = (id, body) =>
  api.put(`/admin/master-products/${id}`, body, { headers: adminHeaders() }).then((r) => r.data);
export const adminDeleteMasterProduct = (id, hard = false) =>
  api.delete(`/admin/master-products/${id}${hard ? "?hard=true" : ""}`, { headers: adminHeaders() }).then((r) => r.data);
export const adminBulkMasterProducts = (ids, action) =>
  api.post("/admin/master-products/bulk", { ids, action }, { headers: adminHeaders() }).then((r) => r.data);
export const adminRenameMasterField = (field, from_value, to_value) =>
  api.post("/admin/master-catalog/rename", { field, from_value, to_value }, { headers: adminHeaders() }).then((r) => r.data);

// ---- Sales ----
export const listSales = (limit = 100) => api.get(`/sales?limit=${limit}`).then((r) => r.data);
export const createSale = (payload) => api.post("/sales", payload).then((r) => r.data);
export const listFiados = () => api.get("/sales/fiados").then((r) => r.data);
export const paySale = (id) => api.post(`/sales/${id}/pay`).then((r) => r.data);

// ---- Stats ----
export const statsToday = () => api.get("/stats/today").then((r) => r.data);

// ---- Price Alerts (AI inflation) ----
export const listPriceAlerts = () => api.get("/price-alerts").then((r) => r.data);
export const simulatePriceAlert = (provider) =>
  api.post(`/price-alerts/simulate${provider ? `?provider=${encodeURIComponent(provider)}` : ""}`).then((r) => r.data);
export const applyPriceAlert = (id) => api.post(`/price-alerts/${id}/apply`).then((r) => r.data);
export const dismissPriceAlert = (id) => api.post(`/price-alerts/${id}/dismiss`).then((r) => r.data);

// ---- Tabs (mesas / cuentas de bar) ----
export const listTabs = (status = "open") => api.get(`/tabs?status=${status}`).then((r) => r.data);
export const getTab = (id) => api.get(`/tabs/${id}`).then((r) => r.data);
export const openTab = (customer_name) => api.post("/tabs/open", { customer_name }).then((r) => r.data);
export const addItemsToTab = (id, items) => api.post(`/tabs/${id}/items`, { items }).then((r) => r.data);
export const closeTab = (id) => api.post(`/tabs/${id}/close`).then((r) => r.data);
export const deleteTab = (id) => api.delete(`/tabs/${id}`).then((r) => r.data);
export const tabVoiceCommand = (text) => api.post("/tabs/voice-command", { text }).then((r) => r.data);
export const sendOrderManual = (customer_name, items_text) =>
  api.post("/tabs/send-order", { customer_name, items_text }).then((r) => r.data);
export const getKitchenWhatsApp = () => api.get("/store/kitchen-whatsapp").then((r) => r.data);
export const setKitchenWhatsApp = (number) => api.put("/store/kitchen-whatsapp", { number }).then((r) => r.data);
export const listKitchenOrders = (limit = 20) => api.get(`/kitchen-orders?limit=${limit}`).then((r) => r.data);
export const adoptCategory = (category, subcategory) =>
  api.post(`/master-products/adopt-category?category=${encodeURIComponent(category)}${subcategory ? `&subcategory=${encodeURIComponent(subcategory)}` : ""}`).then((r) => r.data);

// True if the store's giro belongs to Restaurante/Bar family (unlocks Mesas tab).
export function isBarLikeGiro(giro) {
  if (!giro) return false;
  const g = String(giro).toLowerCase();
  return (
    g.includes("bar") ||
    g.includes("cerveceria") ||
    g.includes("cervecería") ||
    g.includes("fonda") ||
    g.includes("restaurante") ||
    g.includes("depósito") ||
    g.includes("deposito")
  );
}

export function formatMXN(n) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 2,
  }).format(Number(n || 0));
}
